import React from "react";
import PropTypes from "prop-types";
import { formatImportDate } from "../importDate";

const terminalHeading = state =>
  ({
    COMPLETED: "Execution finished",
    COMPLETED_WITH_ERRORS: "Execution finished",
    FAILED: "Import failed",
    RECOVERY_REQUIRED: "Import failed",
  }[state]);

const ExecutionStatus = ({ run, onNewImport, onDownload, onComplete, busy }) => {
  if (
    !run ||
    (run.state === "COMPLETED_WITH_ERRORS" && run.phase === "DRY_RUN") ||
    !["EXECUTION_QUEUED", "EXECUTING", "COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED", "RECOVERY_REQUIRED"].includes(
      run.state,
    )
  ) {
    return null;
  }
  const terminal = ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED", "RECOVERY_REQUIRED"].includes(run.state);
  const reportTerminal =
    ["COMPLETED", "COMPLETED_WITH_ERRORS"].includes(run.state) &&
    (!run.phase || ["EXECUTION", "CLEANUP"].includes(run.phase)) &&
    run.resultWorkflow &&
    run.resultWorkflow.state === "AVAILABLE";
  const counts = run.counts || {};
  const dryRunTerminal = run.state === "COMPLETED_WITH_ERRORS" && run.phase === "DRY_RUN";
  const reportDetails = {
    execution: {
      title: "Execution Report",
      description: "Review the final outcome of each row processed.",
      action: "Download Execution Report",
    },
    failures: {
      title: "Validation Report",
      description: "Review the rows that did not pass dry-run validation.",
      action: "Download Validation Report",
    },
    changes: {
      title: "Change/Diff Report",
      description: "Review the changes recorded during this import.",
      action: "Download Change/Diff Report",
    },
  };
  const reports = (run.artifacts || []).filter(
    artifact => ["execution", "changes"].includes(artifact.type) || (dryRunTerminal && artifact.type === "failures"),
  );
  const summary = dryRunTerminal
    ? [
        ["Rows processed", counts.positions || 0, ""],
        ["Imported successfully", counts.create + counts.update || 0, ""],
        ["Failed", counts.dryRunFail || 0, "import-stat-error"],
      ]
    : [
        ["Rows processed", counts.submitted || 0, ""],
        ["Imported successfully", counts.executionSuccess || 0, ""],
        ["Failed", counts.executionFailure || 0, "import-stat-error"],
      ];

  return (
    <div
      className={`import-workspace import-execution${terminal ? "" : " import-execution-pending"}`}
      data-testid="import-execution"
    >
      <h3>{terminal ? terminalHeading(run.state) : "Your import is being executed"}</h3>
      {terminal && <p className="import-file-meta">{run.filename}</p>}
      {!terminal && (
        <>
          <p>We will email you when execution finishes and your reports are ready in DPM.</p>
          <p className="import-file-meta">
            {counts.submitted || 0}
            {" passing rows submitted"}
            {run.submittedAt && ` · ${formatImportDate(run.submittedAt)}`}
          </p>
          <p className="import-execution-intro">
            You can leave this page or close your browser. The import will continue.
            <br />
            Return to Admin &gt; Import at any time to see your current status.
          </p>
          <p className="import-execution-availability">Passing and failing files from the dry run are no longer available.</p>
        </>
      )}
      {terminal && (
        <div className="import-stats">
          {summary.map(([label, value, className]) => (
            <div className={`import-stat ${className}`} key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      )}
      {reportTerminal && reports.length > 0 && (
        <>
          <div className="import-report-grid">
            {reports.map(artifact => {
              const details = reportDetails[artifact.type];
              return (
                <section className="import-report-card" key={artifact.type}>
                  <h4>{details.title}</h4>
                  <p>{details.description}</p>
                  <button type="button" disabled={busy} onClick={() => onDownload(artifact.type)}>
                    {details.action}
                  </button>
                </section>
              );
            })}
          </div>
          <p className="import-warning">
            <strong>
              Download any reports you want to keep before completing this workflow. After you select Complete and
              return to start screen, these downloads will no longer be available.
            </strong>
          </p>
          <p className="import-file-meta">
            You can leave this page and return to Admin &gt; Import to download your reports until you complete the
            workflow.
          </p>
        </>
      )}
      {reportTerminal && (
        <div className="import-complete">
          <button
            type="button"
            className="import-button-primary"
            data-testid="complete-import-results"
            disabled={busy}
            onClick={onComplete}
          >
            Complete and return to start screen
          </button>
          <small>Returns you to the file upload screen. This does not rerun or reverse the import.</small>
        </div>
      )}
      {["FAILED", "RECOVERY_REQUIRED"].includes(run.state) && (
        <p role="alert">
          {(run.error && run.error.message) ||
            "Import processing ended unsuccessfully. No further processing will occur for this run. Start a corrected import or contact support."}
        </p>
      )}
      {!reportTerminal && terminal && (
        <button
          type="button"
          className="import-button-primary"
          data-testid="start-new-import"
          disabled={busy}
          onClick={onNewImport}
        >
          Return to upload screen
        </button>
      )}
    </div>
  );
};
ExecutionStatus.propTypes = {
  run: PropTypes.shape({ state: PropTypes.string, counts: PropTypes.object }),
  onNewImport: PropTypes.func,
  onDownload: PropTypes.func,
  onComplete: PropTypes.func,
  busy: PropTypes.bool,
};
ExecutionStatus.defaultProps = {
  run: null,
  onNewImport: () => {},
  onDownload: () => {},
  onComplete: () => {},
  busy: false,
};
export default ExecutionStatus;
